import React, { useState, useEffect, useCallback } from 'react';
import api, {
  editShorthandOverride,
  deactivateShorthandOverride,
} from '../../services/api';
import { relativeTime } from './helpers';

// GLOBAL SHORTHAND ALIASES (scope='general') — lifted VERBATIM from the v1
// GlobalKb shorthand section into its own sub-tab under the AI Info "Global
// knowledge" 3-tab shell. Fetches GET /api/aiinfo/global and uses ONLY the
// shorthand[] array (global nicknames were dropped — they make no sense).
// Edit via editShorthandOverride, Remove (soft-deactivate) via
// deactivateShorthandOverride. Non-fatal load: an error shows a banner, never
// blanks the surface. Styling/loading/error-banner patterns mirror v1.

// A global shorthand row as returned by /aiinfo/global.shorthand[]. Typed inline
// (per the collision-avoidance rule: no additions to types/index.ts).
interface GlobalShorthandRow {
  id: number;
  shorthand?: string;
  item_id?: number;
  item_name?: string | null;
  default_quantity?: number | null;
  default_unit?: string | null;
  use_count?: number | null;
  last_used_at?: string | null;
}

const GlobalShorthand: React.FC = () => {
  const [gShorthand, setGShorthand] = useState<GlobalShorthandRow[]>([]);
  const [gLoading, setGLoading] = useState(true);
  const [gError, setGError] = useState<string | null>(null);
  // 's'+id currently being removed (drives per-row disabled + label).
  const [gBusyId, setGBusyId] = useState<string | null>(null);

  // Shorthand edit modal state.
  const [gEdit, setGEdit] = useState<{ id: number } | null>(null);
  const [geShorthand, setGeShorthand] = useState('');
  const [geItemName, setGeItemName] = useState('');
  const [geQty, setGeQty] = useState('');
  const [geUnit, setGeUnit] = useState('');
  const [geSaving, setGeSaving] = useState(false);
  const [geErr, setGeErr] = useState<string | null>(null);

  // Load the global (scope='general') shorthand aliases. Non-fatal. Drops the
  // nicknames array entirely.
  const loadGlobal = useCallback(async () => {
    try {
      setGLoading(true);
      setGError(null);
      const res = await api.get('/aiinfo/global');
      setGShorthand(Array.isArray(res.data?.shorthand) ? res.data.shorthand : []);
    } catch (err) {
      setGError('Failed to load global shorthand aliases');
      console.error(err);
    } finally {
      setGLoading(false);
    }
  }, []);

  useEffect(() => { loadGlobal(); }, [loadGlobal]);

  // Remove (soft-deactivate) a global shorthand alias, then refresh.
  const doDeactivateShorthand = async (id: number) => {
    if (!window.confirm('Remove this global shorthand alias? The AI will stop applying it for all customers.')) return;
    try {
      setGBusyId('s' + id);
      setGError(null);
      await deactivateShorthandOverride(id);
      await loadGlobal();
    } catch (err) {
      setGError('Failed to remove shorthand #' + id);
      console.error(err);
    } finally {
      setGBusyId(null);
    }
  };

  // Open the shorthand edit modal, seeded from the row.
  const openEditShorthand = (s: GlobalShorthandRow) => {
    setGEdit({ id: s.id });
    setGeShorthand(s.shorthand || '');
    setGeItemName(s.item_name || '');
    setGeQty(s.default_quantity != null ? String(s.default_quantity) : '');
    setGeUnit(s.default_unit || '');
    setGeErr(null);
  };

  const saveGEdit = async () => {
    if (!gEdit) return;
    try {
      setGeSaving(true);
      setGeErr(null);
      const qtyRaw = geQty.trim();
      await editShorthandOverride(gEdit.id, {
        shorthand: geShorthand,
        item_name: geItemName,
        default_quantity: qtyRaw === '' || isNaN(Number(qtyRaw)) ? null : Number(qtyRaw),
        default_unit: geUnit.trim() === '' ? null : geUnit.trim(),
      });
      setGEdit(null);
      await loadGlobal();
    } catch (err) {
      setGeErr('Failed to save changes');
      console.error(err);
    } finally {
      setGeSaving(false);
    }
  };

  return (
    <div className="flex-1 overflow-y-auto overflow-x-auto p-3 sm:p-6 bg-gray-50">
      <div className="w-full max-w-[1800px] mx-auto">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h3 className="text-sm font-bold text-gray-900">Global shorthand aliases <span className="font-normal text-gray-500">— apply to every customer</span></h3>
          </div>
          <button onClick={() => loadGlobal()} className="text-xs font-medium text-brand-700 hover:bg-brand-50 px-2 py-1 rounded">Refresh</button>
        </div>
        {gError && (
          <div className="mb-2 bg-red-50 border border-red-200 rounded-lg p-2 text-xs text-red-700 flex items-center justify-between">
            <span>{gError}</span>
            <button onClick={() => setGError(null)} className="text-red-500 hover:text-red-700 ml-3">✕</button>
          </div>
        )}
        {gLoading ? (
          <div className="text-sm text-gray-400 py-3">Loading…</div>
        ) : gShorthand.length === 0 ? (
          <div className="text-sm text-gray-500 bg-white border border-gray-200 rounded-lg p-3">No global shorthand yet. Promote a customer's learned shorthand to “general” to make it apply to all customers.</div>
        ) : (
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-gray-500 uppercase text-xs">
                <tr>
                  <th className="text-left font-medium px-3 py-2">Shorthand</th>
                  <th className="text-left font-medium px-3 py-2">Resolves to</th>
                  <th className="text-left font-medium px-3 py-2">Used</th>
                  <th className="text-right font-medium px-3 py-2">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {gShorthand.map((s) => (
                  <tr key={'gs-' + s.id} className="hover:bg-gray-50">
                    <td className="px-3 py-2 font-medium text-gray-800">{s.shorthand}</td>
                    <td className="px-3 py-2 text-gray-700">{s.item_name || ('item #' + s.item_id)}</td>
                    <td className="px-3 py-2 whitespace-nowrap text-gray-500">
                      <span className="text-gray-700 font-medium">{s.use_count ?? 0}</span>
                      <span className="text-gray-400 ml-1">({relativeTime(s.last_used_at)})</span>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-right">
                      <button onClick={() => openEditShorthand(s)} className="px-2 py-1 text-xs font-medium text-brand-700 hover:bg-brand-50 rounded">Edit</button>
                      <button onClick={() => doDeactivateShorthand(s.id)} disabled={gBusyId === 's' + s.id} className="px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50 rounded disabled:opacity-50">{gBusyId === 's' + s.id ? '…' : 'Remove'}</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Shorthand edit modal */}
      {gEdit && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4" onClick={() => setGEdit(null)}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-base font-bold text-gray-900">Edit global shorthand</h3>
              <button onClick={() => setGEdit(null)} className="text-gray-400 hover:text-gray-600">✕</button>
            </div>

            <div className="px-5 py-4 space-y-4">
              {geErr && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">{geErr}</div>
              )}
              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Shorthand</label>
                <input value={geShorthand} onChange={(e) => setGeShorthand(e.target.value)}
                  className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 text-gray-800 focus:outline-none focus:ring-2 focus:ring-brand-500" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Resolves to (item name)</label>
                <input value={geItemName} onChange={(e) => setGeItemName(e.target.value)}
                  className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 text-gray-800 focus:outline-none focus:ring-2 focus:ring-brand-500" />
              </div>
              <div className="flex gap-3">
                <div className="flex-1">
                  <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Default qty</label>
                  <input value={geQty} onChange={(e) => setGeQty(e.target.value)} inputMode="numeric" placeholder="(none)"
                    className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 text-gray-800 focus:outline-none focus:ring-2 focus:ring-brand-500" />
                </div>
                <div className="flex-1">
                  <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Default unit</label>
                  <input value={geUnit} onChange={(e) => setGeUnit(e.target.value)} placeholder="(none)"
                    className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 text-gray-800 focus:outline-none focus:ring-2 focus:ring-brand-500" />
                </div>
              </div>
            </div>

            <div className="px-5 py-4 border-t border-gray-200 flex justify-end gap-2">
              <button onClick={() => setGEdit(null)} disabled={geSaving}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50">Cancel</button>
              <button onClick={saveGEdit} disabled={geSaving}
                className="px-4 py-2 text-sm font-medium text-white bg-brand-700 hover:bg-brand-800 rounded-lg transition-colors disabled:opacity-50">{geSaving ? 'Saving…' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GlobalShorthand;
